/**
 * useTeacherSettings Hook
 *
 * Syncs teacher settings (whiteboard configurations) between local Zustand store
 * and the database. Provides:
 * - Auto-load settings on login
 * - Auto-save settings on change (debounced)
 * - Graceful error handling with retry
 * - Conflict resolution (server wins on initial load)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { configurationsApi } from '@/lib/api/api';
import { useAppStore } from '@/store';
import type { WB01Config, WB02Config } from '@/store/slices/whiteboardConfigSlice';
import { extractErrorMessage } from './utils';

/**
 * Sync status enum
 */
export type SyncStatus = 'idle' | 'loading' | 'saving' | 'error' | 'synced';

/**
 * Teacher settings hook result
 */
export interface UseTeacherSettingsResult {
  /** Current sync status */
  syncStatus: SyncStatus;
  /** Error message if any */
  error: string | null;
  /** Last successful sync timestamp */
  lastSyncedAt: Date | null;
  /** Whether settings have been loaded from server */
  isLoaded: boolean;
  /** Manual save trigger */
  saveSettings: () => Promise<boolean>;
  /** Manual load trigger */
  loadSettings: () => Promise<boolean>;
  /** Clear error state */
  clearError: () => void;
}

// Debounce delay for auto-save (ms)
const AUTO_SAVE_DELAY = 2000;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

/**
 * Sleep helper for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Hook for syncing teacher settings with the database
 */
export function useTeacherSettings(): UseTeacherSettingsResult {
  // State
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Refs for tracking state
  const isMounted = useRef(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoSaveEnabled = useRef(false);

  // Get store state and actions
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const wb01Config = useAppStore((state) => state.wb01Config);
  const wb02Config = useAppStore((state) => state.wb02Config);
  const presets = useAppStore((state) => state.presets);
  const activePresetId = useAppStore((state) => state.activePresetId);

  // Store setters for applying loaded settings
  const setWB01BoxCount = useAppStore((state) => state.setWB01BoxCount);
  const setWB01HiddenLetters = useAppStore((state) => state.setWB01HiddenLetters);
  const setWB01EnabledDigraphs = useAppStore((state) => state.setWB01EnabledDigraphs);
  const setWB01EnabledVowelTeams = useAppStore((state) => state.setWB01EnabledVowelTeams);
  const setWB01TargetWord = useAppStore((state) => state.setWB01TargetWord);
  const setWB02TapRegionCount = useAppStore((state) => state.setWB02TapRegionCount);
  const setWB02GuideLinesEnabled = useAppStore((state) => state.setWB02GuideLinesEnabled);
  const setWB02GuideLineTypes = useAppStore((state) => state.setWB02GuideLineTypes);
  const setWB02TargetWord = useAppStore((state) => state.setWB02TargetWord);

  /**
   * Apply loaded WB-01 configuration to store
   */
  const applyWB01Config = useCallback((config: WB01Config) => {
    setWB01BoxCount(config.boxCount);
    setWB01HiddenLetters(config.hiddenLetters);
    setWB01EnabledDigraphs(config.enabledDigraphs);
    setWB01EnabledVowelTeams(config.enabledVowelTeams);
    if (config.targetWord && config.targetSegmentation?.length > 0) {
      setWB01TargetWord(config.targetWord, config.targetSegmentation);
    }
  }, [setWB01BoxCount, setWB01HiddenLetters, setWB01EnabledDigraphs, setWB01EnabledVowelTeams, setWB01TargetWord]);

  /**
   * Apply loaded WB-02 configuration to store
   */
  const applyWB02Config = useCallback((config: WB02Config) => {
    setWB02TapRegionCount(config.tapRegionCount);
    setWB02GuideLinesEnabled(config.guideLinesEnabled);
    setWB02GuideLineTypes(config.guideLineTypes);
    if (config.targetWord) {
      setWB02TargetWord(config.targetWord);
    }
  }, [setWB02TapRegionCount, setWB02GuideLinesEnabled, setWB02GuideLineTypes, setWB02TargetWord]);

  /**
   * Load settings from the database with retry
   */
  const loadSettings = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }

    setSyncStatus('loading');
    setError(null);

    let attempt = 0;
    while (attempt < MAX_RETRIES) {
      try {
        const response = await configurationsApi.getAll();

        if (!isMounted.current) return false;

        if (response.status === 'success' && response.data) {
          const { configurations } = response.data;

          // Temporarily disable auto-save while applying loaded settings
          isAutoSaveEnabled.current = false;

          // Apply WB-01 configuration if exists
          if (configurations['wb-01']) {
            const wb01Data = configurations['wb-01'] as unknown as WB01Config;
            applyWB01Config(wb01Data);
          }

          // Apply WB-02 configuration if exists
          if (configurations['wb-02']) {
            const wb02Data = configurations['wb-02'] as unknown as WB02Config;
            applyWB02Config(wb02Data);
          }

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            if (isMounted.current) {
              isAutoSaveEnabled.current = true;
            }
          }, 500);

          setSyncStatus('synced');
          setLastSyncedAt(new Date());
          setIsLoaded(true);
          return true;
        } else {
          throw new Error('Failed to load settings');
        }
      } catch (err) {
        attempt++;
        if (attempt < MAX_RETRIES) {
          await sleep(RETRY_DELAY * attempt);
        } else {
          if (isMounted.current) {
            setSyncStatus('error');
            setError(extractErrorMessage(err));
            // Still mark as loaded so user can continue working
            setIsLoaded(true);
            // Enable auto-save even on load error
            isAutoSaveEnabled.current = true;
          }
          return false;
        }
      }
    }

    return false;
  }, [isAuthenticated, applyWB01Config, applyWB02Config]);

  /**
   * Save current settings to the database with retry
   */
  const saveSettings = useCallback(async (): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }

    setSyncStatus('saving');
    setError(null);

    // Prepare configurations to save
    const configurations: Record<string, Record<string, unknown>> = {
      'wb-01': {
        boxCount: wb01Config.boxCount,
        hiddenLetters: wb01Config.hiddenLetters,
        enabledDigraphs: wb01Config.enabledDigraphs,
        enabledVowelTeams: wb01Config.enabledVowelTeams,
        targetWord: wb01Config.targetWord,
        targetSegmentation: wb01Config.targetSegmentation,
      },
      'wb-02': {
        tapRegionCount: wb02Config.tapRegionCount,
        guideLinesEnabled: wb02Config.guideLinesEnabled,
        guideLineTypes: wb02Config.guideLineTypes,
        targetWord: wb02Config.targetWord,
      },
    };

    let attempt = 0;
    while (attempt < MAX_RETRIES) {
      try {
        const response = await configurationsApi.saveAll(configurations);

        if (!isMounted.current) return false;

        if (response.status === 'success') {
          setSyncStatus('synced');
          setLastSyncedAt(new Date());
          return true;
        } else {
          throw new Error('Failed to save settings');
        }
      } catch (err) {
        attempt++;
        if (attempt < MAX_RETRIES) {
          await sleep(RETRY_DELAY * attempt);
        } else {
          if (isMounted.current) {
            setSyncStatus('error');
            setError(extractErrorMessage(err));
          }
          return false;
        }
      }
    }

    return false;
  }, [isAuthenticated, wb01Config, wb02Config]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
    if (syncStatus === 'error') {
      setSyncStatus('idle');
    }
  }, [syncStatus]);

  /**
   * Debounced auto-save when settings change
   */
  useEffect(() => {
    if (!isAuthenticated || !isLoaded || !isAutoSaveEnabled.current) {
      return;
    }

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule new save
    saveTimeoutRef.current = setTimeout(() => {
      if (isMounted.current && isAutoSaveEnabled.current) {
        saveSettings();
      }
    }, AUTO_SAVE_DELAY);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [isAuthenticated, isLoaded, wb01Config, wb02Config, presets, activePresetId, saveSettings]);

  /**
   * Load settings when user logs in
   */
  useEffect(() => {
    if (isAuthenticated && !isLoaded) {
      loadSettings();
    }

    // Reset state when user logs out
    if (!isAuthenticated && isLoaded) {
      setIsLoaded(false);
      setSyncStatus('idle');
      setError(null);
      setLastSyncedAt(null);
      isAutoSaveEnabled.current = false;
    }
  }, [isAuthenticated, isLoaded, loadSettings]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    syncStatus,
    error,
    lastSyncedAt,
    isLoaded,
    saveSettings,
    loadSettings,
    clearError,
  };
}
